import { useEffect, useState, useMemo } from "react";
import { PromotionalMessage } from "../../../types/prodcatBaseTypes";
import styles from "./PromotionalCountdownTimer.module.scss";

type Props = {
  year: number;
  month: number;
  day: number;
  message: PromotionalMessage;
  availableToOrder: boolean;
  showPromoTimerVariant: string;
};

type time = {
  hours: number;
  minutes: number;
  seconds: number;
};

const promoTypesForCountdown = [
  "Save % with end date",
  "Save % - Selected with end date",
];
const promoGroup = "Save";
const messageType = "promotional";

const formatTimer = (hours: number, minutes: number, seconds: number) => {
  let countdownString = "";

  if (hours === 0 && minutes !== 0) {
    countdownString = `${minutes}m ${seconds}s`;
  } else if (hours === 0 && minutes === 0) {
    countdownString = `${seconds}s`;
  } else {
    countdownString = `${hours}h ${minutes}m ${seconds}s`;
  }
  return countdownString;
};

const shouldRenderCountdown = (
  countDown: time,
  message: PromotionalMessage,
  availableToOrder: boolean,
  showPromoTimerVariant: string,
) => {
  if (
    message.type !== messageType ||
    message.promotionGroup !== promoGroup ||
    !promoTypesForCountdown.includes(message.promotionType)
  ) {
    return false;
  }

  if (!availableToOrder) {
    return false;
  }

  // if all of the values are 0 the end date is now
  if (
    countDown.hours === 0 &&
    countDown.minutes === 0 &&
    countDown.seconds === 0
  ) {
    return false;
  }

  if (showPromoTimerVariant !== "A" && showPromoTimerVariant !== "B") {
    return false;
  }
  if (showPromoTimerVariant === "A" && countDown.hours >= 72) {
    return false;
  }
  if (showPromoTimerVariant === "B" && countDown.hours >= 48) {
    return false;
  }
  return true;
};

const calculateTimeLeft = (countDownDate: Date) => {
  const difference = countDownDate.getTime() - Date.now();

  if (difference <= 0) {
    return { hours: 0, minutes: 0, seconds: 0 };
  }

  return {
    hours: Math.floor(difference / (1000 * 60 * 60)),
    minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((difference % (1000 * 60)) / 1000),
  };
};

export const PromotionalCountdownTimer = ({
  year,
  month,
  day,
  message,
  availableToOrder,
  showPromoTimerVariant,
}: Props) => {
  const [isMounted, setIsMounted] = useState(false);
  const countDownDate = useMemo(() => {
    return new Date(year, month, day, 23, 45, 0, 0); // add additional time to take end to 23:45:00 on end date - when promotions end
  }, []);
  const [timeLeft, setTimeLeft] = useState(() =>
    calculateTimeLeft(countDownDate),
  );

  useEffect(() => {
    setIsMounted(true); //fixes react hydration issue
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(countDownDate));
      if (
        timeLeft.hours <= 0 &&
        timeLeft.minutes <= 0 &&
        timeLeft.seconds <= 0
      ) {
        clearInterval(timer);
      }
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [countDownDate]);

  if (!isMounted) {
    return null;
  }

  if (
    !shouldRenderCountdown(
      timeLeft,
      message,
      availableToOrder,
      showPromoTimerVariant,
    )
  ) {
    return null;
  }

  return (
    <p
      data-testid="product:promotional-countdown-timer"
      className={styles.promoCountdown}
    >
      {`Ends in ${formatTimer(
        timeLeft.hours,
        timeLeft.minutes,
        timeLeft.seconds,
      )}`}
    </p>
  );
};
